import React, {MouseEventHandler, useState} from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
} from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {Link} from 'react-router-dom';
import {SubmissionStatusType} from './types';
import {useReviewContext} from '../../contexts/ReviewContext';
import {SaltusAny} from '../../utils/SaltusAny';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';

interface ConfirmationModalProps {
  handleClose:
    | MouseEventHandler<HTMLButtonElement>
    | ((event: SaltusAny, reason: 'backdropClick' | 'escapeKeyDown') => void)
    | undefined;
  open: boolean;
  title: string;
  message: React.ReactNode;
  cancelText?: string;
  confirmText?: string;
  confirmAction: () => void;
  loading?: boolean;
  type?: SubmissionStatusType;
}

export const ConfirmationModal = (props: ConfirmationModalProps) => {
  const {
    open,
    handleClose,
    title,
    message,
    cancelText = 'Cancel',
    confirmText = 'OK',
    confirmAction,
    loading,
    type,
  } = props;
  const {reviewValidation: review} = useReviewContext();

  const [state, setState] = useState({
    advice: false,
    mandates: false,
    fees: false,
  });

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({
      ...state,
      [event.target.name]: event.target.checked,
    });
  };
  const {recommendsGridUrl} = useRequirementsDentistOrBAU();
  const {mandates, fees} = state;
  const isConfirmDisabled = !mandates || !fees;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{message}</DialogContentText>
        <br />
        {/* Render only if type is 'allRequirementsFinalised' */}
        {type === 'allRequirementsFinalised' && (
          <>
            <DialogContentText>
              Prior to the final submission, carefully assess the following high-risk
              sections:
            </DialogContentText>
            <FormControl component="fieldset" sx={{m: 3}} variant="standard">
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mandates}
                      onChange={handleChange}
                      name="mandates"
                    />
                  }
                  label="The client has been setup on the correct model"
                  data-test="checkbox-mandates"
                />
                <FormControlLabel
                  control={
                    <Checkbox checked={fees} onChange={handleChange} name="fees" />
                  }
                  label="The initial and ongoing fees are correct"
                  data-test="checkbox-fees"
                />
              </FormGroup>
            </FormControl>
            <Link
              to={{
                pathname: `/account/${review?.account?.id}/review-journey/${review?.id}/${recommendsGridUrl}`,
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <DialogContentText>
                If you wish to make any amendments to any of the above click here
              </DialogContentText>
            </Link>
            <br />
            <DialogContentText>
              By completing this review, you attest that the provided advice is precise
              and will therefore be fully implemented.
            </DialogContentText>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          disabled={loading}
          onClick={handleClose as MouseEventHandler<HTMLButtonElement>}
          color="secondary"
        >
          {cancelText}
        </Button>
        <Button
          onClick={confirmAction}
          autoFocus
          color="primary"
          variant="contained"
          disabled={
            type === 'allRequirementsFinalised' ? isConfirmDisabled || loading : loading
          }
          data-test="confirm-button"
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
